import { apiClient } from './api/apiClient';
import { API_ENDPOINTS } from '../config';
import { BlogPost, BlogQuery, PaginatedResponse, ApiResponse } from '../types';
import { buildQueryString } from '../utils/queryBuilder';

/**
 * Blog Posts Service
 * Handles all blog-related API operations
 */
export class BlogService {
  /**
   * Fetch all blog posts with optional filters
   */
  static async getAllBlogPosts(
    query?: BlogQuery
  ): Promise<ApiResponse<PaginatedResponse<BlogPost>>> {
    const queryString = buildQueryString(query);
    const endpoint = `${API_ENDPOINTS.BLOG_POSTS}${queryString}`;
    return apiClient.get<PaginatedResponse<BlogPost>>(endpoint, false);
  }

  /**
   * Fetch IELTS blog posts (for landing page)
   */
  static async getIELTSBlogPosts(limit: number = 4): Promise<ApiResponse<PaginatedResponse<BlogPost>>> {
    const query: BlogQuery = {
      blogType: 'IELTS',
      limit,
      sort: '-createdAt',
    };
    return this.getAllBlogPosts(query);
  }

  /**
   * Fetch announcement posts
   */
  static async getAnnouncementPosts(limit: number = 10): Promise<ApiResponse<PaginatedResponse<BlogPost>>> {
    const query: BlogQuery = {
      blogType: 'Announcement',
      limit,
      sort: '-createdAt',
    };
    return this.getAllBlogPosts(query);
  }

  /**
   * Fetch single blog post by ID
   */
  static async getBlogPostById(id: string): Promise<ApiResponse<BlogPost>> {
    return apiClient.get<BlogPost>(API_ENDPOINTS.BLOG_POST_BY_ID(id), false);
  }

  /**
   * Fetch blog post by slug
   */
  static async getBlogPostBySlug(slug: string): Promise<ApiResponse<PaginatedResponse<BlogPost>>> {
    const query: BlogQuery = {
      limit: 1,
    };
    const queryString = buildQueryString(query);
    const endpoint = `${API_ENDPOINTS.BLOG_POSTS}${queryString}&where[slug][equals]=${slug}`;
    return apiClient.get<PaginatedResponse<BlogPost>>(endpoint, false);
  }

  /**
   * Fetch related blog posts by type
   */
  static async getRelatedPosts(
    blogType: string,
    excludeId: string,
    limit: number = 3
  ): Promise<ApiResponse<PaginatedResponse<BlogPost>>> {
    const query: BlogQuery = {
      blogType: blogType as any,
      limit,
    };
    const queryString = buildQueryString(query);
    const endpoint = `${API_ENDPOINTS.BLOG_POSTS}${queryString}&exclude=${excludeId}`;
    return apiClient.get<PaginatedResponse<BlogPost>>(endpoint, false);
  }

  /**
   * Create new blog post (admin only)
   */
  static async createBlogPost(data: Partial<BlogPost>): Promise<ApiResponse<BlogPost>> {
    return apiClient.post<BlogPost>(API_ENDPOINTS.BLOG_POSTS, data as any, true);
  }

  /**
   * Update blog post (admin only)
   */
  static async updateBlogPost(
    id: string,
    data: Partial<BlogPost>
  ): Promise<ApiResponse<BlogPost>> {
    return apiClient.patch<BlogPost>(
      API_ENDPOINTS.BLOG_POST_BY_ID(id),
      data as any,
      true
    );
  }

  /**
   * Delete blog post (admin only)
   */
  static async deleteBlogPost(id: string): Promise<ApiResponse<{ success: boolean }>> {
    return apiClient.delete<{ success: boolean }>(
      API_ENDPOINTS.BLOG_POST_BY_ID(id),
      true
    );
  }

  /**
   * Search blog posts
   */
  static async searchBlogPosts(searchTerm: string): Promise<ApiResponse<PaginatedResponse<BlogPost>>> {
    const query: BlogQuery = {
      search: searchTerm,
      limit: 20,
    };
    return this.getAllBlogPosts(query);
  }
}

export default BlogService;
